import type { SavedReportTableRow } from '../lib/useReportsTable'
import type { SavedReportsActionsHandlers } from './SavedReportsActionsDropdown'

import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { LayersIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	useDeleteSavedFilterMutation,
	useSavedFiltersQuery,
} from '@/entities/report'

import { MasterTable, SRT_TableToolbar } from '@/shared/components/master-table'
import NoData from '@/shared/components/placeholders/NoData'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
} from '@/shared/components/ui/breadcrumb'
import { confirm } from '@/shared/components/ui/confirmer'
import { Content, Header, Layout } from '@/shared/components/ui/layout'
import { Separator } from '@/shared/components/ui/separator'
import { SavedFilterType } from '@/shared/generated'
import { useModals } from '@/shared/lib/modal-manager'
import { useAssortmentTableConfigStore } from '@/shared/lib/table-config'
import { getFiltersQueryString } from '@/shared/tools/location'
import {
	actDeleted,
	errorMessage,
	processingMessage,
	successActionMessage,
} from '@/shared/tools/message'

import { useReportsTable } from '../lib/useReportsTable'

export function SavedReportsPage() {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const queryClient = useQueryClient()
	const tableConfig = useAssortmentTableConfigStore()
	const savedFiltersQuery = useSavedFiltersQuery<SavedReportTableRow[]>()
	const deleteSavedFilter = useDeleteSavedFilterMutation({
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ['saved-filters'],
			}),
	})

	const onDeleteClick = async ({ is_global, id }: SavedReportTableRow) => {
		const answer = await confirm({
			title: t('general_detete_confirm_title'),
			description: t('general_detete_confirm_subtitle'),
			actionText: t('general_delete', {
				ns: 'keys',
			}),
			cancelText: t('general_cancel', {
				ns: 'keys',
			}),
			ActionProps: {
				variant: 'destructive',
			},
		})
		if (!answer) return
		toast.promise(deleteSavedFilter.mutateAsync(id), {
			loading: processingMessage(t),
			success: successActionMessage('Report', actDeleted(t), t),
			error: errorMessage(t),
		})
	}
	const onEditClick = (report: SavedReportTableRow) =>
		showModal('UPDTAE_ASSORTMENT_REPORT', { report })
	const onDownloadClick = (report: SavedReportTableRow) => {
		if (report.type === SavedFilterType.Products) {
			showModal('EXPORT_ASSORTMENT', {
				requireCallback: false,
				defaultName: report.name,
				tableConfig,
				context: {
					filters: report.filters,
				},
			})
		} else if (report.type === SavedFilterType.Anchors) {
			showModal('EXPORT_ANCHORS_TABLE', {
				filters: report.filters,
				reportName: report.name,
			})
		}
	}
	const onOpenClick = (report: SavedReportTableRow) => {
		if (report.type === SavedFilterType.Products) {
			window.open(`/p/products${getFiltersQueryString(report.filters)}`)
		} else if (report.type === SavedFilterType.Anchors) {
			window.open(
				`/p/anchor-management/assortment${getFiltersQueryString(report.filters)}`
			)
		}
	}
	const isLoading = savedFiltersQuery.isLoading
	const isFetched = savedFiltersQuery.isFetched
	const handlers: SavedReportsActionsHandlers = {
		onDeleteClick,
		onEditClick,
		onDownloadClick,
		onOpenClick,
	}
	const rows = useMemo(
		() => savedFiltersQuery.data || [],
		[savedFiltersQuery.data]
	)
	const table = useReportsTable({ rows, handlers, isLoading })

	return (
		<Layout className='relative h-full'>
			<Header className='h-11 items-center border-b px-4 py-2'>
				<LayersIcon className='size-4' />
				<Separator orientation='vertical' className='mx-2 h-5' />
				<Breadcrumb className='text-sm'>
					<BreadcrumbList>
						<BreadcrumbItem>
							<BreadcrumbPage>{t('saved_reports.page.title')}</BreadcrumbPage>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>
			</Header>

			<Layout className='relative h-full px-4 py-3'>
				<div className='flex size-full flex-col fade-in'>
					{rows.length === 0 && isFetched ? (
						<div className='border-base flex h-full rounded-lg border border-solid'>
							<NoData
								subtitle={t('reports_no_data_subtitle', {
									defaultValue: 'Try to change filters or search query',
								})}
							/>
						</div>
					) : (
						<Content className='flex h-full flex-1 flex-col'>
							<div className='pb-2'>
								<SRT_TableToolbar table={table} />
							</div>
							<div className='border-base mb-4 flex h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
								<MasterTable table={table} />
							</div>
						</Content>
					)}
				</div>
			</Layout>
		</Layout>
	)
}
